import { createSelector } from 'reselect'
import { RootState } from '~/app/store/types'
import { UserInfo } from './types'

const selectUserInfo = (state: RootState) => ({
  ...state.userInfo.userInfo,
  isUserInfoInitiated: state.userInfo.isUserInfoInitiated,
})
const selectUserVerificationStatus = createSelector(
  [selectUserInfo],
  userInfo => userInfo.verificationStatus
)

const selectUserName = createSelector(
  [selectUserInfo],
  userInfo => userInfo.fullName || userInfo.firstName
)

const selectUserEmail = createSelector([selectUserInfo], userInfo => userInfo.email)

const selectUserProfile = (state: RootState) => state.userInfo.userProfile

const selectUserAvatarUrl = createSelector(
  [selectUserProfile],
  userProfile => userProfile?.avatarUrl
)

const selectUserUsdVirtualAccount = createSelector(
  [selectUserInfo],
  userInfo => userInfo?.usdcExtension?.virtualAccount
)

const getAccVerified = (userDetails: UserInfo) => userDetails.accountFullyVerified
const getKycVerifying = (userDetails: UserInfo) => userDetails.kycInformationVerifying
const getKycRejected = (userDetails: UserInfo) => userDetails.kycRejectedReason

export const verificationMapping = createSelector(
  [getAccVerified, getKycVerifying, getKycRejected],
  (accFullyVerified, kycInfoVerifying, kycRejectReason) => {
    type verifiedStatus = 'verified' | 'unverified' | 'pending' | 'rejected'
    let status: verifiedStatus = 'unverified'

    if (accFullyVerified) {
      status = 'verified'
    } else if (kycInfoVerifying) {
      status = 'pending'
    } else if (!accFullyVerified && !kycInfoVerifying && kycRejectReason) {
      status = 'rejected'
    } else if (!accFullyVerified && !kycInfoVerifying && !kycRejectReason) {
      status = 'unverified'
    }
    return status
  }
)

const selectUserFirstName = createSelector([selectUserInfo], userInfo => userInfo.firstName)
const selectUserLastName = createSelector([selectUserInfo], userInfo => userInfo.lastName)
const selectUserFullName = createSelector([selectUserInfo], userInfo => userInfo.fullName)

const selectUserFullNameChecking = createSelector(
  [selectUserFirstName, selectUserLastName, selectUserFullName],
  (userFirstName, userLastName, userFullName) => {
    if (!userFirstName || !userLastName) {
      return userFullName
    } else {
      return userFirstName + ' ' + userLastName
    }
  }
)

const selectUserRequiresManualPeriodicReview = createSelector(
  [selectUserInfo],
  userInfo => userInfo.periodicReviewRequired && !userInfo.automaticPeriodicReviewEnabled
)

const selectUserRequiresAutoPeriodicReview = createSelector(
  [selectUserInfo],
  userInfo => userInfo.periodicReviewRequired && userInfo.automaticPeriodicReviewEnabled
)

const selectIsUserPeriodicReviewOverdueAndAutoPeriodicReviewEnabled = createSelector(
  [selectUserInfo],
  userInfo => userInfo.automaticPeriodicReviewEnabled && userInfo.periodicReviewOverdue
)

export {
  selectIsUserPeriodicReviewOverdueAndAutoPeriodicReviewEnabled,
  selectUserInfo,
  selectUserVerificationStatus,
  selectUserProfile,
  selectUserAvatarUrl,
  selectUserName,
  selectUserEmail,
  selectUserFirstName,
  selectUserLastName,
  selectUserFullName,
  selectUserFullNameChecking,
  selectUserRequiresManualPeriodicReview,
  selectUserRequiresAutoPeriodicReview,
  selectUserUsdVirtualAccount,
}
